import React, { useRef } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
// @ts-ignore
import batiment from "../assets/batiment.mp4";
// @ts-ignore
import solar from "../assets/solar.mp4";

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

interface SolutionItem {
    id: string;
    title: string;
    subtitle: string;
    text: string;
    video: string;
    reverse?: boolean;
    accent: string;
    number: string;
}

// ─── Carte individuelle ────────────────────────────────────────────────────────
const SolutionCard: React.FC<{ sol: SolutionItem; index: number }> = ({ sol, index }) => {
    const ref = useRef<HTMLDivElement>(null);

    // once: false → apparaît à l'entrée, disparaît à la sortie
    const isInView = useInView(ref, { once: false, margin: "-100px" });

    // Parallax léger sur la vidéo uniquement
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const videoY = useTransform(scrollYProgress, [0, 1], [30, -30]);

    const textVariants = {
        hidden: {
            opacity: 0,
            x: sol.reverse ? 60 : -60,
            filter: "blur(6px)",
        },
        visible: {
            opacity: 1,
            x: 0,
            filter: "blur(0px)",
            transition: { duration: 1.0, ease: [0.16, 1, 0.3, 1] },
        },
    };

    const videoVariants = {
        hidden: {
            opacity: 0,
            x: sol.reverse ? -60 : 60,
            scale: 0.94,
            filter: "blur(8px)",
        },
        visible: {
            opacity: 1,
            x: 0,
            scale: 1,
            filter: "blur(0px)",
            transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.12 },
        },
    };

    // Stagger sur les lignes de texte
    const lineVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: (i: number) => ({
            opacity: 1,
            y: 0,
            transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.25 + i * 0.1 },
        }),
    };

    return (
        <Box
            ref={ref}
            position="relative"
            py={{ base: 12, lg: 20 }}
        >
            {/* Numéro de section en arrière-plan */}
            <MotionBox
                position="absolute"
                top={{ base: "0", lg: "50%" }}
                left={sol.reverse ? "auto" : { base: "0", lg: "-20px" }}
                right={sol.reverse ? { base: "0", lg: "-20px" } : "auto"}
                transform={{ lg: "translateY(-50%)" }}
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 1, delay: 0.5 }}
                zIndex={0}
                pointerEvents="none"
                userSelect="none"
            >
                <Text
                    fontSize={{ base: "120px", lg: "200px" }}
                    fontWeight="900"
                    lineHeight="1"
                    color="rgba(255,255,255,0.025)"
                    letterSpacing="-0.05em"
                >
                    {sol.number}
                </Text>
            </MotionBox>

            <Flex
                direction={{ base: "column", lg: sol.reverse ? "row-reverse" : "row" }}
                align="center"
                justify="space-between"
                gap={{ base: 12, lg: 20 }}
                position="relative"
                zIndex={1}
            >
                {/* ── Côté texte ── */}
                <MotionBox
                    flex="1"
                    maxW={{ lg: "460px" }}
                    variants={textVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                >
                    {/* Label */}
                    <motion.div
                        variants={lineVariants}
                        initial="hidden"
                        animate={isInView ? "visible" : "hidden"}
                        custom={0}
                    >
                        <Flex align="center" gap={3} mb={6}>
                            <Box w="24px" h="1px" bg={sol.accent} opacity={0.8} />
                            <Text
                                fontSize="10px"
                                letterSpacing="0.35em"
                                textTransform="uppercase"
                                color={sol.accent}
                                fontWeight="500"
                                opacity={0.9}
                            >
                                {sol.subtitle}
                            </Text>
                        </Flex>
                    </motion.div>

                    {/* Titre principal */}
                    <motion.div
                        variants={lineVariants}
                        initial="hidden"
                        animate={isInView ? "visible" : "hidden"}
                        custom={1}
                    >
                        <Text
                            fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                            fontWeight="200"
                            color="white"
                            mb={6}
                            letterSpacing="-0.03em"
                            lineHeight="1.1"
                        >
                            {sol.title.split(" ").map((word, i, arr) =>
                                i === arr.length - 1 ? (
                                    <Box as="span" key={i} fontWeight="800">
                                        {word}
                                    </Box>
                                ) : (
                                    `${word} `
                                )
                            )}
                        </Text>
                    </motion.div>

                    {/* Séparateur animé */}
                    <motion.div
                        initial={{ scaleX: 0, opacity: 0 }}
                        animate={isInView ? { scaleX: 1, opacity: 1 } : { scaleX: 0, opacity: 0 }}
                        transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        style={{ originX: 0, marginBottom: "1.75rem" }}
                    >
                        <Box
                            h="1px"
                            w="64px"
                            bg={`linear-gradient(90deg, ${sol.accent}, transparent)`}
                        />
                    </motion.div>

                    {/* Corps du texte */}
                    <motion.div
                        variants={lineVariants}
                        initial="hidden"
                        animate={isInView ? "visible" : "hidden"}
                        custom={3}
                    >
                        <Text
                            fontSize={{ base: "md", md: "lg" }}
                            color="whiteAlpha.600"
                            lineHeight="2"
                            fontWeight="300"
                            letterSpacing="0.01em"
                            maxW="400px"
                        >
                            {sol.text}
                        </Text>
                    </motion.div>
                </MotionBox>

                {/* ── Côté vidéo ── */}
                <MotionBox
                    flex="1"
                    maxW={{ base: "100%", lg: "540px" }}
                    w="100%"
                    variants={videoVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                    position="relative"
                >
                    {/* Halo coloré derrière */}
                    <Box
                        position="absolute"
                        inset="-30px"
                        bg={`radial-gradient(ellipse at center, ${sol.accent}20 0%, transparent 65%)`}
                        filter="blur(24px)"
                        pointerEvents="none"
                        borderRadius="50%"
                    />

                    {/* Cadre vidéo organique */}
                    <MotionBox
                        style={{ y: videoY }}
                        borderRadius={{
                            base: "28px",
                            md: sol.reverse
                                ? "55% 45% 40% 60% / 50% 50% 50% 50%"
                                : "45% 55% 60% 40% / 50% 50% 50% 50%",
                        }}
                        overflow="hidden"
                        position="relative"
                        boxShadow={`0 48px 96px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.07)`}
                        transition="box-shadow 0.6s ease"
                        _hover={{
                            boxShadow: `0 56px 112px rgba(0,0,0,0.65), 0 0 0 1px ${sol.accent}40`,
                        }}
                        sx={{ aspectRatio: "4/3" }}
                    >
                        <video
                            autoPlay
                            loop
                            muted
                            playsInline
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                filter: "saturate(0.7) brightness(0.88)",
                                display: "block",
                            }}
                        >
                            <source src={sol.video} type="video/mp4" />
                        </video>

                        {/* Reflet de lumière en haut */}
                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            h="35%"
                            bg="linear-gradient(180deg, rgba(255,255,255,0.08) 0%, transparent 100%)"
                            pointerEvents="none"
                        />

                        {/* Dégradé coloré en bas */}
                        <Box
                            position="absolute"
                            bottom={0}
                            left={0}
                            right={0}
                            h="40%"
                            bg={`linear-gradient(0deg, ${sol.accent}18 0%, transparent 100%)`}
                            pointerEvents="none"
                        />
                    </MotionBox>
                </MotionBox>
            </Flex>
        </Box>
    );
};

// ─── Composant principal ───────────────────────────────────────────────────────
const Solutions: React.FC = () => {
    const { t } = useTranslation();
    const headerRef = useRef(null);
    const headerInView = useInView(headerRef, { once: false, margin: "-80px" });

    const solutions: SolutionItem[] = [
        {
            id: "solar",
            number: "01",
            title: t("solutionTitre1"),
            subtitle: t("solutionSubtitle1"),
            text: t("solutionTexte"),
            video: solar,
            accent: "#C9A84C",
        },
        {
            id: "batiment",
            number: "02",
            title: t("solutionTitre2"),
            subtitle: t("solutionSubtitle2"),
            text: t("solutionTexte2"),
            video: batiment,
            reverse: true,
            accent: "#8B9E6E",
        },
    ];

    return (
        <Flex
            direction="column"
            align="center"
            bg="#03111F"
            px={{ base: 6, md: 12, lg: 20 }}
            py={{ base: 24, md: 40 }}
            width="100%"
            position="relative"
            overflow="hidden"
        >
            {/* Ambient top glow */}
            <Box
                position="absolute"
                top="-180px"
                left="50%"
                transform="translateX(-50%)"
                w="900px"
                h="450px"
                bg="radial-gradient(ellipse, rgba(201,168,76,0.05) 0%, transparent 70%)"
                pointerEvents="none"
            />

            {/* ── Header section ── */}
            <Box ref={headerRef} textAlign="center" mb={{ base: 20, md: 32 }} w="100%">
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={headerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                >
                    <Text
                        fontSize="10px"
                        letterSpacing="0.4em"
                        textTransform="uppercase"
                        color="whiteAlpha.400"
                        mb={5}
                        fontWeight="400"
                    >
                        {t("solutionTitre")}
                    </Text>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    animate={headerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 32 }}
                    transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                >
                    <Text
                        fontSize={{ base: "4xl", md: "6xl", lg: "7xl" }}
                        fontWeight="200"
                        color="white"
                        letterSpacing="-0.04em"
                        lineHeight="1.05"
                        mb={6}
                    >
                        {t("solutionsHeading1")}{" "}
                        <Box as="span" fontWeight="800">
                            {t("solutionsHeading2")}
                        </Box>
                    </Text>
                </motion.div>

                <motion.div
                    initial={{ scaleX: 0, opacity: 0 }}
                    animate={headerInView ? { scaleX: 1, opacity: 1 } : { scaleX: 0, opacity: 0 }}
                    transition={{ duration: 0.9, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
                    style={{ originX: 0.5 }}
                >
                    <Box
                        w="56px"
                        h="1px"
                        bg="linear-gradient(90deg, #C9A84C, #8B9E6E)"
                        mx="auto"
                        opacity={0.7}
                    />
                </motion.div>
            </Box>

            {/* ── Cards ── */}
            <Flex
                direction="column"
                gap={{ base: 4, lg: 8 }}
                maxW="1200px"
                w="100%"
            >
                {solutions.map((sol, i) => (
                    <SolutionCard key={sol.id} sol={sol} index={i} />
                ))}
            </Flex>
        </Flex>
    );
};

export default Solutions;